import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, ShoppingCart, Eye, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import { fmtNum } from '@/utils/currency';

export default function Suppliers() {
  const { token } = useContext(AuthContext);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPOs, setSupplierPOs] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  };

  const fetchSupplierPOs = async (supplierId) => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierPOs(response.data);
    } catch (error) {
      toast.error('Failed to fetch supplier purchase orders');
    }
  };

  const handleAddSupplier = () => {
    setIsEditMode(false);
    setFormData({ name: '', address: '', contact: '' });
    setIsDialogOpen(true);
  };

  const handleEditSupplier = (supplier) => {
    setIsEditMode(true);
    setFormData({
      id: supplier.id,
      name: supplier.name,
      address: supplier.address || '',
      contact: supplier.contact || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditMode) {
        await axios.put(`${API}/suppliers/${formData.id}`, {
          name: formData.name,
          address: formData.address,
          contact: formData.contact
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier updated successfully');
      } else {
        await axios.post(`${API}/suppliers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier created successfully');
      }
      
      setIsDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!confirm('Are you sure you want to delete this supplier?')) return;
    
    try {
      await axios.delete(`${API}/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supplier deleted successfully');
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const handleViewSupplier = async (supplier) => {
    setSelectedSupplier(supplier);
    await fetchSupplierPOs(supplier.id);
  };

  const handleBackToList = () => {
    setSelectedSupplier(null);
    setSupplierPOs([]);
  };

  if (selectedSupplier) {
    return (
      <Layout>
        <div className="p-6">
          <Button onClick={handleBackToList} variant="outline" className="mb-4">
            ← Back to Suppliers
          </Button>

          <Card className="p-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{selectedSupplier.name}</h2>
                {selectedSupplier.address && <p className="text-slate-600">📍 {selectedSupplier.address}</p>}
                {selectedSupplier.contact && <p className="text-slate-600">📞 {selectedSupplier.contact}</p>}
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">Total Purchases</p>
                <p className="text-3xl font-bold text-emerald-600">${fmtNum(selectedSupplier.total_purchases_usd)}</p>
                <p className="text-sm text-slate-500">{selectedSupplier.po_count} Purchase Orders</p>
              </div>
            </div>
          </Card>

          <h3 className="text-xl font-bold mb-4">Purchase Orders</h3>
          
          <div className="space-y-4">
            {supplierPOs.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-slate-500">No purchase orders found</p>
              </Card>
            ) : (
              supplierPOs.map((po) => (
                <Card key={po.id} className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-lg font-bold">{po.po_number}</h4>
                      <p className="text-sm text-slate-600">
                        {new Date(po.date).toLocaleDateString()} • {po.category_name}
                      </p>
                      <p className="text-sm text-slate-500">{po.items.length} items</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {po.currency === 'USD' ? '$' : po.currency === 'MVR' ? 'ރ' : '¥'}
                        {fmtNum(po.total)}
                      </p>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        po.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {po.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </Layout>
    );
  }

  const handleExportPDF = () => {
    const rows = suppliers.map((s, i) => [
      i + 1,
      s.name || '-',
      s.contact || '-',
      s.address || '-',
    ]);
    exportListPDF({
      title: 'Suppliers',
      headers: ['#', 'Name', 'Contact', 'Address'],
      rows,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Suppliers</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportPDF} data-testid="suppliers-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddSupplier}>
                <Plus className="w-4 h-4 mr-2" />
                Add Supplier
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{isEditMode ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Supplier Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Contact</Label>
                  <Input
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="Phone / Email"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    {isEditMode ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="grid gap-4">
          {suppliers.length === 0 ? (
            <Card className="p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">No suppliers yet</p>
              <Button onClick={handleAddSupplier}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Supplier
              </Button>
            </Card>
          ) : (
            suppliers.map((supplier) => (
              <Card key={supplier.id} className="p-6 hover:shadow-lg transition-all">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="w-6 h-6 text-blue-600" />
                      <h3 className="text-xl font-bold text-slate-900">{supplier.name}</h3>
                    </div>
                    {supplier.address && <p className="text-sm text-slate-600 mb-1">📍 {supplier.address}</p>}
                    {supplier.contact && <p className="text-sm text-slate-600">📞 {supplier.contact}</p>}
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Total Purchases (USD)</p>
                    <p className="text-3xl font-bold text-emerald-600 mb-1">
                      ${fmtNum(supplier.total_purchases_usd || 0)}
                    </p>
                    <p className="text-sm text-slate-500 mb-3">{supplier.po_count || 0} Purchase Orders</p>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewSupplier(supplier)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View POs
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditSupplier(supplier)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteSupplier(supplier.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
